import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { ProgressBar } from 'primereact/progressbar';
import DateInput from '../common/DateInput';
import DriverFilters, { useDriverList } from '../common/DriverFilters';
import { EmptyState } from '../common/PageHeader';
import { useAlerts } from '../../custom/Alerts';
import { sameDay, addDays } from '../../utils/dates';
import { driversApi, listiciApi } from '../../api/services';
import { activityLabel, driverLabel } from '../../utils/activities';
import useObrada from './useObrada';
import DiscView from './DiscView';
import NamestiListic from './NamestiListic';
import TraceStrip from './TraceStrip';
import DanListica from './DanListica';
import { izPrevlacenja, izInputa, vozacIzNaziva, predloziIzNaziva } from './folder';
import { napraviPlan, zapisListica, daniZaSnimanje, predlogDatuma, aktivnostiOf, segmentiOf, pomeri, izmenaDana, IZVOR_LABEL, opisKartice, ulaganjeOf, postojeceNaTraci } from './plan';
import { naive, fmtMin, fmtNaive, toAppDate, beogradOffset, DAY_MS } from './engine/vreme';
import { fmtDuration } from '../../utils/dates';

/* srednja traka = raspoloživost (zavisi od baždarenja tahografa); ostali rad / pauzu operater menja na listiću (slika ili tabela) */
const srednja = 'availability';

/** razlika (prosečan log P po tokenu) prvog i drugog kandidata iznad koje AI sam postavlja vozača */
const uDataUrl = (url) => fetch(url).then((r) => r.blob()).then((bl) => new Promise((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(fr.result);
    fr.onerror = rej;
    fr.readAsDataURL(bl);
}));

/** godina datuma: rukom pisane cifre godine su nepouzdane (26 se čita kao 25, 20, 23), a listići se skeniraju posle vožnje —
    tekuća godina, osim ako bi datum bio u budućnosti (tada prethodna) */
const godinaDatuma = (m, d) => {
    const danas = new Date();
    const y = danas.getFullYear();
    return new Date(y, m - 1, d) > danas ? y - 1 : y;
};

/** bod rukopisa za konkretan datum ulaganja (d1 + d2; vađenje isti ili sledeći dan prema traci); 0 bez AI čitanja */
/** AI čita ime samo kad vozač nije prepoznat iz naziva fajla/foldera */

const KAZNA_DANA = 10; // log P po preskočenom danu: niz ima prednost, rukopis pomera samo uz vrlo jak dokaz (cifre se pogrešno čitaju "sigurno")
function ocenaDatuma(item, datum) {
    const a = item && item.aiDat;
    if (!a || !a.d1) return 0;
    const p2 = (n) => String(n).padStart(2, '0');
    const i = a.dm.indexOf(`${p2(datum.getDate())}.${p2(datum.getMonth() + 1)}`);
    if (i < 0) return -1e3;
    const prelaz = item.result && item.result.prazno && item.ulaganje >= item.result.vadjenje ? 1 : 0;
    return a.d1[i] + (i + prelaz < a.dm.length ? a.d2[i + prelaz] : 0);
}

/** najverovatniji datumi aktivnosti iz rukopisa (5); vađenje = isti ili sledeći dan prema traci */
function kandidatiDatuma(item) {
    const a = item && item.aiDat;
    if (!a || !a.d1) return null;
    const prelaz = item.result && item.result.prazno && item.ulaganje >= item.result.vadjenje ? 1 : 0;
    const zbir = a.dm.map((_, i) => (i + prelaz < a.dm.length ? a.d1[i] + a.d2[i + prelaz] : -1e9));
    return [...zbir.keys()].sort((x, y) => zbir[y] - zbir[x]).slice(0, 5)
        .map((i) => { const [d, m] = a.dm[i].split('.').map(Number); return { m, datum: new Date(godinaDatuma(m, d), m - 1, d) }; })
        .filter((x) => x.datum.getMonth() === x.m - 1) // 29.02 van prestupne godine otpada
        .map((x) => x.datum);
}


let seq = 0;
const novaStavka = (file, driver) => ({
    key: `l${++seq}`, file, name: file.name, status: 'obrada', error: null, result: null, sha256: null, urls: {},
    driver, datumOd: null, ulaganje: 0, provera: null, stanje: 'novo',
});

/** ključ provere: vozač + period listića — promena bilo čega traži novu proveru */
const kljucProvere = (item, plan) => (item.driver && plan ? `${item.driver.id}|${plan.utc.od}|${plan.utc.do}` : null);

const STANJE = {
    sacuvan: { label: 'Sačuvan', cls: 'ok', icon: 'pi pi-check-circle' },
    zamenjen: { label: 'Zamenjen', cls: 'ok', icon: 'pi pi-check-circle' },
    izbaciti: { label: 'Za izbacivanje', cls: 'warn', icon: 'pi pi-trash' },
    preskocen: { label: 'Preskočen', cls: 'neutral', icon: 'pi pi-forward' },
};

/** status stavke za spisak */
function statusOf(item, plan) {
    if (item.status === 'obrada') return { label: 'Obrada…', cls: 'neutral', icon: 'pi pi-spin pi-spinner' };
    if (item.status === 'greska') return { label: 'Greška', cls: 'danger', icon: 'pi pi-times-circle' };
    if (STANJE[item.stanje]) return STANJE[item.stanje];
    if (!item.driver) return { label: 'Nema vozača', cls: 'neutral', icon: 'pi pi-user' };
    if (!item.datumOd) return { label: 'Nema datuma', cls: 'neutral', icon: 'pi pi-calendar' };
    if (item.predlozen && !item.videno) return { label: 'Predložen datum — pogledajte listić', cls: 'warn', icon: 'pi pi-eye' };
    /* prazan listić (ceo krug "nije uložen"): nema perioda za unos ni proveru — ranije je stajalo "Provera…" zauvek (Bogdanović 005) */
    if (item.result && !plan) return { label: 'Prazan listić', cls: 'warn', icon: 'pi pi-ban' };
    if (!item.provera || item.provera.kljuc !== kljucProvere(item, plan)) return { label: 'Provera…', cls: 'neutral', icon: 'pi pi-spin pi-spinner' };
    if (plan && plan.kartica) return { label: 'Preko kartice', cls: 'danger', icon: 'pi pi-id-card' };
    if (plan && plan.konflikt) return { label: 'Već postoji', cls: 'danger', icon: 'pi pi-exclamation-triangle' };
    return { label: 'Spreman', cls: 'info', icon: 'pi pi-circle' };
}

/* Bulk unos: više fotografija listića -> prepoznavanje (motor u Web Workeru) -> pregled strelicama -> snimanje. */
/* jedan = stranica "Taho listići": isti motor i pregled za JEDNU sliku (bez foldera, spiska i masovnog unosa) */
export default function BulkUnos({ active, jedan = false }) {
    const alerts = useAlerts();
    const list = useDriverList([]);
    const [items, setItems] = useState([]);
    const [idx, setIdx] = useState(0);
    const [bulkDriver, setBulkDriver] = useState(null);
    const [busy, setBusy] = useState(false);
    const [folder, setFolder] = useState(null);
    const fileRef = useRef(null);
    const [prevlaci, setPrevlaci] = useState(false);
    /* AI čitanje datuma sa rukopisa može da se isključi — sporo je na slabijem serveru; izbor se pamti */
    const [aiUkljucen, setAiUkljucen] = useState(() => { try { return localStorage.getItem('bulkAi') !== '0'; } catch { return true; } });
    const promeniAi = (v) => { setAiUkljucen(v); try { localStorage.setItem('bulkAi', v ? '1' : '0'); } catch { /* privatni režim */ } };

    const update = useCallback((key, patch) => setItems((all) => all.map((x) => (x.key === key ? { ...x, ...(typeof patch === 'function' ? patch(x) : patch) } : x))), []);
    /* zadržavanje izbora pri listanju: poslednji izabran vozač / datum prelazi na listić na koji se pređe */
    const [zadrzi, setZadrzi] = useState({ vozac: true, datum: false });
    const poslednji = useRef({ driver: null, datumOd: null });
    const izmenaForme = useCallback((key, patch) => {
        if (patch.driver !== undefined) { poslednji.current.driver = patch.driver; setBulkDriver(patch.driver); patch = { ...patch, driverIzvor: 'rucno' }; }
        if (patch.datumOd !== undefined) poslednji.current.datumOd = patch.datumOd;
        update(key, patch);
    }, [update]);

    const obrada = useObrada((key, msg) => {
        if (!msg.ok) return update(key, { status: 'greska', error: msg.error });
        /* ponovna obrada (ručni položaj listića): stare slike se oslobađaju, izmene na listiću važe za stari položaj pa se brišu */
        update(key, (x) => {
            Object.values(x.urls || {}).forEach((u) => URL.revokeObjectURL(u));
            const ponovo = !!x.result;
            return { status: 'spreman', result: msg.result, sha256: msg.sha256, urls: msg.urls, width: msg.width, height: msg.height, ulaganje: msg.result.ulaganje,
                ...(ponovo ? { aktivnosti: null, rucno: null, naciniDana: null } : {}) };
        });
    });

    /* oslobađanje slika iz memorije pri uklanjanju stavki */
    const itemsRef = useRef(items);
    itemsRef.current = items;
    useEffect(() => () => itemsRef.current.forEach((x) => Object.values(x.urls || {}).forEach((u) => URL.revokeObjectURL(u))), []);

    /* bulk = jedan folder: sve slike iz njega (i podfoldera), redom po putanji — bira se u FolderDialog (prevlačenje ili disk) */
    const ucitajFolder = async ({ naziv, slike, preskoceno }) => {
        if (!slike.length) { alerts.warn(`U folderu "${naziv}" nema slika listića (JPG, PNG, BMP, WEBP).`, 'Folder'); return; }
        if (!jedan && !list.companyIds.length) { alerts.warn('Prvo izaberite kompaniju — vozači se prepoznaju samo među vozačima izabrane kompanije.', 'Kompanija'); return; }
        if (!jedan && items.length && !(await alerts.confirm('Novi folder', `Trenutni bulk (${items.length} listića) biće zamenjen folderom "${naziv}" (${slike.length} slika). Već uneti listići ostaju u bazi.`))) return;
        obrada.otkazi();
        items.forEach((x) => Object.values(x.urls || {}).forEach((u) => URL.revokeObjectURL(u)));
        /* ime u spisku = putanja unutar foldera; vozač se PRVO mapira po nazivu fajla/foldera (vozači izabrane kompanije) */
        const nove = slike.map((s) => {
            const v = vozacIzNaziva(s.putanja, list.drivers);
            const x = { ...novaStavka(s.file, v || bulkDriver), name: s.putanja.split('/').slice(1).join('/') || s.file.name, putanja: s.putanja };
            return v ? { ...x, driverIzvor: 'naziv' } : x;
        });
        setFolder({ naziv, slika: slike.length, preskoceno });
        setItems(nove);
        setIdx(0);
        obrada.dodaj(nove.map((x) => ({ key: x.key, file: x.file })));
    };

    const planovi = useMemo(() => {
        const m = new Map();
        /* greška u planu jednog listića ne obara ekran: taj listić nema plan (poruka u konzoli) */
        items.forEach((x) => { try { m.set(x.key, napraviPlan(x, srednja)); } catch (e) { console.error("Plan listića", x.name, e); m.set(x.key, null); } }); // eslint-disable-line no-console
        return m;
    }, [items]);

    const cur = items[idx] || null;

    /* udvojena posada: listići sa istim vremenom ulaganja i vađenja (±15 min) moraju imati isti datum */
    const parovi = useMemo(() => {
        const m = new Map();
        items.forEach((x) => {
            if (!x.result || !x.result.prazno || !x.datumOd) return;
            const par = items.find((y) => y !== x && y.result && y.result.prazno && y.datumOd
                && Math.abs(y.ulaganje - x.ulaganje) <= 15 && Math.abs(y.result.vadjenje - x.result.vadjenje) <= 15
                && !sameDay(y.datumOd, x.datumOd));
            if (par) m.set(x.key, par);
        });
        return m;
    }, [items]);
    const plan = cur ? planovi.get(cur.key) : null;

    /* provera postojećih aktivnosti za tekući listić (i posle svake promene vozača/datuma/ulaganja) */
    const proveri = useCallback(async (item, p) => {
        const kljuc = kljucProvere(item, p);
        if (!kljuc) return null;
        /* aktivnosti dana: period listića i ceo UTC dan izabranog datuma (prikaz "već u bazi" ne zavisi od početka aktivnosti) */
        const dan = naive(item.datumOd);
        const od = toAppDate(Math.min(p.utc.od, dan)), kraj = toAppDate(Math.max(p.utc.do, dan + DAY_MS) - 1);
        const [check, dani] = await Promise.all([
            listiciApi.check({ driverId: item.driver.id, from: fmtApi(p.utc.od), to: fmtApi(p.utc.do), sha256: item.sha256 }),
            driversApi.activities(item.driver.id, od, kraj),
        ]);
        const izvori = {};
        (check.days || []).forEach((dd) => { izvori[naive(new Date(dd.activityDate))] = dd.izvor; });
        const provera = { kljuc, izvori, postojeci: dani || [], duplikat: check.duplikat || null };
        update(item.key, { provera });
        return provera;
    }, [update]);

    /* listić koji je bio prikazan: predloženi datum je operater video */
    const curKey = cur ? cur.key : null;
    useEffect(() => { if (curKey && cur && !cur.videno && cur.status === 'spreman') update(curKey, { videno: true }); }, [curKey, cur && cur.status]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!cur || STANJE[cur.stanje] || cur.status === 'greska') return;
        const p = {};
        const { driver, datumOd } = poslednji.current;
        /* vozač koji je prepoznao AI ili izabrao operater na tom listiću se ne prepisuje */
        if (zadrzi.vozac && driver && (!cur.driver || cur.driver.id !== driver.id) && !['ai', 'ai-folder', 'naziv', 'rucno'].includes(cur.driverIzvor)) Object.assign(p, { driver, provera: null, driverIzvor: 'zadrzi' });
        if (zadrzi.datum && datumOd && (!cur.datumOd || cur.datumOd.getTime() !== datumOd.getTime())) Object.assign(p, { datumOd, predlozen: false, datumIzvor: 'zadrzi' });
        if (Object.keys(p).length) update(cur.key, p);
    }, [curKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const curKljuc = cur ? kljucProvere(cur, plan) : null;
    useEffect(() => {
        if (!active || !cur || !curKljuc || (cur.provera && cur.provera.kljuc === curKljuc) || STANJE[cur.stanje]) return undefined;
        const t = setTimeout(() => { proveri(cur, plan).catch(() => {}); }, 250);
        return () => clearTimeout(t);
    }, [active, curKljuc]); // eslint-disable-line react-hooks/exhaustive-deps

    /* strelice levo/desno listaju listiće (osim dok se kuca u polje) */
    useEffect(() => {
        const onKey = (e) => {
            if (!active || !items.length) return;
            const tag = (e.target && e.target.tagName) || '';
            if (/INPUT|TEXTAREA|SELECT/.test(tag) || (e.target && e.target.isContentEditable)) return;
            if (e.key === 'ArrowLeft') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
            if (e.key === 'ArrowRight') { e.preventDefault(); setIdx((i) => Math.min(items.length - 1, i + 1)); }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [active, items.length]);

    /* posle snimanja: provere istog vozača više ne važe (dani su se promenili) */
    const ponistiProvere = (driverId) => setItems((all) => all.map((x) => (x.driver && x.driver.id === driverId ? { ...x, provera: null } : x)));

    const snimi = async (item, p, replace) => {
        await listiciApi.save({
            driverId: item.driver.id, replace, days: daniZaSnimanje(p), listic: zapisListica(item, p),
            ponisti: replace ? (p.ponisti || []).map((x) => x.id) : [],
        });
        update(item.key, { stanje: replace ? 'zamenjen' : 'sacuvan' });
        ponistiProvere(item.driver.id);
    };
    const izbaci = async (item, p) => {
        await listiciApi.izbaci({ driverId: item.driver.id, listic: zapisListica(item, p) });
        update(item.key, { stanje: 'izbaciti' });
    };

    const run = async (fn) => {
        setBusy(true);
        try { await fn(); } catch (e) { if (e && e.message && !e.isAxiosError) alerts.error(e.message); }
        setBusy(false);
    };

    const sacuvajTekuci = (replace) => run(async () => {
        if (!plan) throw new Error('Unesite datum aktivnosti i vozača');
        if (plan.neispravno) throw new Error('Suma aktivnosti ručno izmenjenog dana mora biti 24h');
        if (plan.prazno) throw new Error('Nijedan dan listića nije izabran za unos');
        /* listić preko kartice (vožnja / rad / raspoloživost) se ne unosi — ništa se ne menja, operater bira (Izbaci listić / Preskoči) */
        if (plan.kartica) {
            alerts.warn(`Unos nije moguć — listić se preklapa sa aktivnostima sa kartice (${opisKartice(plan)}). Aktivnosti sa kartice se ne menjaju; listić možete izbaciti.`, 'Preko kartice');
            return;
        }
        const prethodni = plan.ponisti || [];
        const tekst = prethodni.length
            ? `Ovaj listić zamenjuje ${prethodni.length === 1 ? 'prethodni listić' : `${prethodni.length} prethodna listića`}: ${prethodni.map((x) => x.listic || `#${x.id}`).join(', ')}. `
              + 'Sve aktivnosti tog listića (i na drugim danima) postaju odmor, pa se upisuje ovaj listić. Aktivnosti sa kartice se ne menjaju. Nastaviti?'
            : 'Postojeće aktivnosti u periodu listića biće zamenjene aktivnostima sa listića (za dane gde je izabrano „Zameni listićem“). Aktivnosti sa kartice se ne menjaju. Nastaviti?';
        if (replace && !(await alerts.confirm('Zamena aktivnosti', tekst, { severity: 'danger' }))) return;
        await snimi(cur, plan, replace);
        alerts.success(replace ? 'Aktivnosti listića su upisane preko postojećih' : 'Aktivnosti listića su sačuvane');
        if (idx < items.length - 1) setIdx(idx + 1);
    });

    const izbaciTekuci = () => run(async () => {
        if (!plan) throw new Error('Unesite datum aktivnosti i vozača');
        await izbaci(cur, plan);
        alerts.info('Listić je zabeležen za izbacivanje', 'Izbacivanje');
        if (idx < items.length - 1) setIdx(idx + 1);
    });

    /* sve spremne bez konflikta: sveža provera pred svako snimanje (listići istog vozača se mogu preklapati) */
    const masovno = (sKonfliktom) => run(async () => {
        let ok = 0, preskoceno = 0, kartica = 0;
        for (const item of itemsRef.current) {
            const p = napraviPlan(item, srednja);
            if (item.status !== 'spreman' || STANJE[item.stanje] || !item.driver || !p || p.neispravno || p.prazno || (item.predlozen && !item.videno)) { preskoceno++; continue; } // eslint-disable-line no-continue
            const provera = await proveri(item, p);
            const sveze = napraviPlan({ ...item, provera }, srednja);
            /* preko kartice: uvek na izbacivanje */
            if (sveze.kartica) { await izbaci(item, sveze); kartica++; continue; } // eslint-disable-line no-continue
            if (sveze.konflikt !== sKonfliktom) { preskoceno++; continue; } // eslint-disable-line no-continue
            if (sKonfliktom) await izbaci(item, sveze); else await snimi(item, sveze, false);
            ok++;
        }
        if (kartica) alerts.warn(`Preko kartice (zabeleženo za izbacivanje): ${kartica}.`, 'Preko kartice');
        if (sKonfliktom) alerts.info(`Za izbacivanje zabeleženo: ${ok}. Preskočeno: ${preskoceno}.`, 'Izbacivanje');
        else alerts.success(`Sačuvano listića: ${ok}. Preskočeno (konflikt, nedostaje vozač/datum, nepregledan predloženi datum ili već obrađeno): ${preskoceno}.`);
    });

    const primeniVozaca = () => {
        if (!bulkDriver) return alerts.error('Izaberite vozača');
        setItems((all) => all.map((x) => (STANJE[x.stanje] ? x : { ...x, driver: bulkDriver, driverIzvor: 'bulk', provera: null })));
        return alerts.info(`Vozač ${driverLabel(bulkDriver)} je postavljen na sve neobrađene listiće.`, 'Vozač');
    };

    const ukloni = () => {
        if (!cur) return;
        Object.values(cur.urls || {}).forEach((u) => URL.revokeObjectURL(u));
        setItems((all) => all.filter((x) => x.key !== cur.key));
        setIdx((i) => Math.max(0, Math.min(i, items.length - 2)));
    };

    /* ---------- vozač (bez AI) ----------
       Vozač se uzima samo iz naziva fajla/foldera (naziv je obično tačno ime vozača iz baze); kad se ne poklapa tačno,
       nude se tri najsličnija vozača izabranih kompanija po nazivu (predloziIzNaziva). AI čita samo datum. */
    const firmaKljuc = list.companyIds.length ? JSON.stringify([...list.companyIds].map((x) => (x == null ? 'bez' : x)).sort()) : null;
    /* vozač iz naziva fajla/foldera: najpouzdaniji izvor — mapira se prvi (već pri učitavanju foldera, i ponovo kad se promeni
       kompanija), AI ga ne čita; ne dira samo ručni izbor i vozača izabranog za ceo bulk. Naziv bez vozača kompanije -> AI. */
    useEffect(() => {
        if (!list.drivers.length) return;
        let promena = false;
        const all = items.map((x) => {
            if (STANJE[x.stanje] || ['rucno', 'bulk'].includes(x.driverIzvor)) return x;
            const v = vozacIzNaziva(x.putanja || x.name, list.drivers);
            if (!v) return x.driverIzvor === 'naziv' ? (promena = true, { ...x, driver: null, driverIzvor: null, provera: null }) : x;
            if (x.driver && x.driver.id === v.id && x.driverIzvor === 'naziv') return x;
            promena = true;
            return { ...x, driver: v, driverIzvor: 'naziv', provera: null };
        });
        if (promena) setItems(all);
    }, [items, list.drivers]); // eslint-disable-line react-hooks/exhaustive-deps
    const [, setAiGreska] = useState(false);
    const [, setAiTik] = useState(0);

    /* ---------- AI datum aktivnosti ----------
       Za svaki listić: bodovi rukopisa za sve dan.mesec (ulaganje i vađenje). Godina = tekuća (datum ne sme biti u budućnosti; cifre godine iz rukopisa su nepouzdane).
       Datum = nastavak prethodnog listića istog vozača ako ga rukopis potvrđuje (među 5 najverovatnijih), inače
       najverovatniji iz rukopisa (vađenje = isti ili sledeći dan, prema traci). Predložen datum je označen dok ga operater
       ne pogleda; datum koji je operater uneo ili pogledao se više ne menja. */
    const datRadi = useRef(false);
    const [datGreska, setDatGreska] = useState(false);
    useEffect(() => {
        if (!aiUkljucen || datRadi.current || datGreska) return;
        const sledeci = items.find((x) => x.status === 'spreman' && !STANJE[x.stanje] && x.urls.aiD1 && x.urls.aiD2 && !x.aiDat);
        if (!sledeci) return;
        datRadi.current = true;
        (async () => {
            try {
                const body = { d1: await uDataUrl(sledeci.urls.aiD1), d2: await uDataUrl(sledeci.urls.aiD2) };
                if (sledeci.urls.aiG1) body.g1 = await uDataUrl(sledeci.urls.aiG1);
                if (sledeci.urls.aiG2) body.g2 = await uDataUrl(sledeci.urls.aiG2);
                update(sledeci.key, { aiDat: await listiciApi.procitajDatume(body) });
            } catch {
                setDatGreska(true);
                update(sledeci.key, { aiDat: { greska: true } });
            } finally {
                datRadi.current = false;
                setAiTik((t) => t + 1);
            }
        })();
    }, [items, datGreska, aiUkljucen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!aiUkljucen) return;
        const all = items.map((x) => ({ ...x }));
        let promena = false;
        all.forEach((x, i) => {
            if (STANJE[x.stanje] || x.status !== 'spreman') return;
            if (x.datumOd && x.datumIzvor !== 'ai') return; // uneo operater / zadržan
            if (x.datumIzvor === 'ai' && x.videno && i !== idx) return; // pogledan
            /* niz ima prednost: listići istog vozača idu redom — očekivani datum je dan posle vađenja prethodnog.
               Rukopis (cifre su nepouzdane, npr. "4" kao "9") bira samo razmak: očekivani dan ili do 14 dana kasnije
               (slobodni dani), uz kaznu po preskočenom danu. Bez prethodnog listića — najverovatniji iz rukopisa. */
            const pr = predlogDatuma(all, i, (y) => napraviPlan(y, srednja));
            let d = null, nacin = 'rukopis';
            if (pr) {
                let najbolji = -Infinity;
                for (let g = 0; g <= 14; g++) {
                    const dan = new Date(pr.datum.getFullYear(), pr.datum.getMonth(), pr.datum.getDate() + g);
                    const s = ocenaDatuma(x, dan) - KAZNA_DANA * g;
                    if (s > najbolji) { najbolji = s; d = dan; }
                }
                nacin = 'niz';
            } else {
                const kand = kandidatiDatuma(x);
                if (!kand || !kand.length) return;
                d = kand[0];
            }
            if (!x.datumOd || !sameDay(x.datumOd, d)) {
                all[i] = { ...x, datumOd: d, datumIzvor: 'ai', datumNacin: nacin, predlozen: true, videno: i === idx, provera: null };
                promena = true;
            }
        });
        if (promena) setItems(all);
    }, [items, aiUkljucen]); // eslint-disable-line react-hooks/exhaustive-deps

    /* reset: briše učitani bulk i vraća formu na početak (uneti listići ostaju u bazi) */
    const reset = async () => {
        if (items.length && !(await alerts.confirm('Reset', 'Obrisati sve učitane listiće i vratiti formu na početak? Već uneti listići ostaju u bazi.'))) return;
        obrada.otkazi();
        items.forEach((x) => Object.values(x.urls || {}).forEach((u) => URL.revokeObjectURL(u)));
        setItems([]); setIdx(0); setFolder(null); setBulkDriver(null);
        setZadrzi({ vozac: true, datum: false });
        poslednji.current = { driver: null, datumOd: null };
        setAiGreska(false); setDatGreska(false);
        list.changeCompanies([]);
    };

    /* vozač samo iz spiska vozača izabranih kompanija: promena kompanije poništava vozača koji nije u njima */
    useEffect(() => {
        const ids = new Set(list.drivers.map((d) => d.id));
        setItems((all) => (all.some((x) => x.driver && !STANJE[x.stanje] && !ids.has(x.driver.id))
            ? all.map((x) => (x.driver && !STANJE[x.stanje] && !ids.has(x.driver.id) ? { ...x, driver: null, provera: null } : x)) : all));
        setBulkDriver((d) => (d && !ids.has(d.id) ? null : d));
        if (poslednji.current.driver && !ids.has(poslednji.current.driver.id)) poslednji.current.driver = null;
    }, [list.drivers]);

    /* napredak AI čitača (vozač samo uz izabranu kompaniju) */
    const aiNapredak = useMemo(() => {
        const sp = items.filter((x) => x.status === 'spreman' && !STANJE[x.stanje]);
        const datumi = sp.filter((x) => !x.urls.aiD1 || x.aiDat).length;
        const ukupno = sp.length;
        const gotovo = datumi;
        return { n: sp.length, datumi, ukupno, gotovo, radi: aiUkljucen && (gotovo < ukupno || items.some((x) => x.status === 'obrada')) };
    }, [items, firmaKljuc, aiUkljucen]);

    const driverOptions = list.drivers.map((d) => ({ label: driverLabel(d), value: d }));
    const gotovo = items.filter((x) => x.status !== 'obrada').length;
    const stat = useMemo(() => {
        const s = { spreman: 0, konflikt: 0, obradjen: 0 };
        items.forEach((x) => {
            const st = statusOf(x, planovi.get(x.key));
            if (STANJE[x.stanje]) s.obradjen++;
            else if (st.cls === 'danger' && x.status === 'spreman') s.konflikt++;
            else if (st.cls === 'info') s.spreman++;
        });
        return s;
    }, [items, planovi]);

    const field = (label, id, control, extra) => (
        <div className="field">
            <label htmlFor={id}>{label}</label>
            {control}
            {extra}
        </div>
    );

    return (
        <div className="bulk-page"
            onDragOver={(e) => { if (Array.from(e.dataTransfer.types || []).includes('Files')) { e.preventDefault(); if (!prevlaci) setPrevlaci(true); } }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPrevlaci(false); }}
            onDrop={async (e) => { e.preventDefault(); setPrevlaci(false); ucitajFolder(await izPrevlacenja(e.dataTransfer)); }}>
            {/* izbor foldera sa diska: odmah se učitava (Chrome pre toga sam pita "Upload … files" — to se ne može ukloniti) */}
            {jedan ? (
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.bmp,.webp,image/*" hidden
                    onChange={(e) => { const izbor = izInputa(e.target.files); e.target.value = ''; ucitajFolder(izbor); }} />
            ) : (
                <input ref={(el) => { fileRef.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
                    type="file" multiple hidden onChange={(e) => { const izbor = izInputa(e.target.files); e.target.value = ''; ucitajFolder(izbor); }} />
            )}
            {prevlaci && (
                <div className="folder-overlay" aria-hidden="true">
                    {jedan
                        ? <div><i className="pi pi-image" /><b>Pustite sliku listića</b><span>učitava se odmah</span></div>
                        : <div><i className="pi pi-folder-open" /><b>Pustite folder sa listićima</b><span>učitava se odmah, bez pitanja pregledača</span></div>}
                </div>
            )}
            <section className="panel">
                <div className="panel-head">
                    <div>
                        <h3 className="panel-title">{jedan ? 'Listić' : 'Listići'}</h3>
                        <p className="panel-sub">{jedan ? <>{cur ? <>Slika <b>{cur.name}</b>. </> : 'Učitajte sliku listića (dugme ili prevlačenje na ekran); kompanija pomaže AI-ju da prepozna vozača. '}</> : folder
                            ? <>Folder <b>{folder.naziv}</b>: {folder.slika} slika{folder.preskoceno ? `, preskočeno ${folder.preskoceno} fajlova koji nisu slike` : ''}.</>
                            : 'Izaberite kompaniju, pa folder sa listićima (dugme ili prevlačenje foldera na ekran) — učitavaju se sve slike iz njega, redom po imenu.'} Vreme sa listića je beogradsko; aktivnosti se upisuju u UTC.</p>
                    </div>
                    <div className="panel-actions">
                        {jedan && <Button label={cur ? 'Druga slika' : 'Učitaj sliku'} icon="pi pi-image" onClick={() => fileRef.current && fileRef.current.click()} />}
                        {/* folder tek posle izbora kompanije: vozač se po nazivu traži među vozačima izabrane kompanije */}
                        {!jedan && (
                            <span className="tt-omotac">
                                <Button label="Izaberi folder" icon="pi pi-folder-open" disabled={!list.companyIds.length} onClick={() => fileRef.current && fileRef.current.click()}
                                    tooltip={list.companyIds.length ? 'Učitava sve slike listića iz foldera (ili prevucite folder na ekran)' : 'Prvo izaberite kompaniju — vozači se prepoznaju samo među vozačima izabrane kompanije'}
                                    tooltipOptions={{ position: 'bottom', showOnDisabled: true }} />
                            </span>
                        )}
                        <Button label="Reset" icon="pi pi-refresh" outlined severity="secondary" disabled={busy} onClick={reset} />
                        {!jedan && <Button label="Sačuvaj sve bez konflikta" icon="pi pi-check" outlined disabled={!stat.spreman || busy} onClick={() => masovno(false)} />}
                        {!jedan && (
                            <Button label="Konflikte za izbacivanje" icon="pi pi-trash" outlined severity="warning" disabled={!stat.konflikt || busy}
                                onClick={() => masovno(true)} />
                        )}
                    </div>
                </div>
                <DriverFilters list={list} showDriver={false}>
                    <div className="field grow">
                        <label htmlFor="bulk-driver">Vozač (tekući listić)</label>
                        <div className="row-inline">
                            <Dropdown inputId="bulk-driver" value={cur && !STANJE[cur.stanje] ? cur.driver : bulkDriver} options={driverOptions} dataKey="id" filter showClear
                                onChange={(e) => {
                                    /* povezano sa poljem "Vozač" tekućeg listića */
                                    if (cur && !STANJE[cur.stanje]) izmenaForme(cur.key, { driver: e.value, provera: null });
                                    else { setBulkDriver(e.value); poslednji.current.driver = e.value; }
                                }}
                                placeholder="Izaberite vozača" emptyMessage="Nema pronađenih vozača" virtualScrollerOptions={driverOptions.length > 10 ? { itemSize: 40 } : undefined} />
                            {!jedan && <Button type="button" label="Primeni na sve" outlined severity="secondary" disabled={!bulkDriver || !items.length} onClick={primeniVozaca} />}
                        </div>
                    </div>
                    <div className="field inline-field bulk-ai-opcija">
                        <label className="check" htmlFor="bulk-ai" title="AI čita datum aktivnosti sa rukopisa na listiću (vozač se uzima iz naziva fajla). Isključite ako je sporo — datum tada unosite ručno.">
                            <InputSwitch inputId="bulk-ai" checked={aiUkljucen} onChange={(e) => promeniAi(e.value)} />
                            <span><i className="pi pi-sparkles" aria-hidden="true" /> AI čitanje datuma</span>
                        </label>
                    </div>
                    {!jedan && (
                    <div className="field inline-field bulk-zadrzi">
                        <label className="check" htmlFor="bulk-zv"><Checkbox inputId="bulk-zv" checked={zadrzi.vozac} onChange={(e) => setZadrzi((z) => ({ ...z, vozac: e.checked }))} /> Zadrži vozača pri listanju</label>
                        <label className="check" htmlFor="bulk-zd"><Checkbox inputId="bulk-zd" checked={zadrzi.datum} onChange={(e) => setZadrzi((z) => ({ ...z, datum: e.checked }))} /> Zadrži datum pri listanju</label>
                    </div>
                    )}
                </DriverFilters>
                {items.length > 0 && aiNapredak.ukupno > 0 && aiNapredak.radi && (
                    <div className="bulk-progress ai" role="status" aria-live="polite">
                        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                        <ProgressBar value={Math.round((aiNapredak.gotovo / aiNapredak.ukupno) * 100)} showValue={false} style={{ height: '.4rem' }} />
                        <span><i className="pi pi-sparkles" aria-hidden="true" /> AI čita datume: {aiNapredak.datumi}/{aiNapredak.n}</span>
                    </div>
                )}
                {items.length > 0 && !jedan && (
                    <div className="bulk-progress">
                        <ProgressBar value={Math.round((gotovo / items.length) * 100)} showValue={false} style={{ height: '.4rem' }} />
                        <span>Obrađeno {gotovo}/{items.length} · <b>uneto {items.filter((x) => x.stanje === 'sacuvan' || x.stanje === 'zamenjen').length}</b> · za izbacivanje {items.filter((x) => x.stanje === 'izbaciti').length} · nije uneto {items.filter((x) => !STANJE[x.stanje] || x.stanje === 'preskocen').length} · već postoji {stat.konflikt}</span>
                    </div>
                )}
            </section>

            {!jedan && items.some((x) => !x.driver && !STANJE[x.stanje]) && (
                <div className="callout warn bulk-callout" role="status">
                    <i className="pi pi-user" />
                    <span>Vozač se ne prepoznaje sa listića (ime je pisano rukom). Izaberite vozača i kliknite <b>Primeni na sve</b>;
                        za udvojenu posadu promenite vozača na njegovim listićima. Bez vozača: {items.filter((x) => !x.driver && !STANJE[x.stanje]).length}.</span>
                </div>
            )}

            {!items.length && jedan ? (
                <div className="panel">
                    <EmptyState icon="pi pi-image" title="Učitajte sliku listića"
                        text="Isti motor kao u Bulk listićima: pronalazi listić na slici (Kienzle, BLU-8-125 DUAL, BLU-2-125-EC), poravnava skalu i čita aktivnosti. Sečenje i rotacija nisu potrebni.">
                        <div className="steps">
                            <div className="step"><span className="step-num">1</span><div><b>Slika</b><span>Učitajte sliku ili je prevucite na ekran (najbolje skener, ceo listić).</span></div></div>
                            <div className="step"><span className="step-num">2</span><div><b>Provera</b><span>Proverite aktivnosti na slici i u tabeli; po potrebi namestite listić ručno.</span></div></div>
                            <div className="step"><span className="step-num">3</span><div><b>Unos</b><span>Datum i vozač, pa Unesi — aktivnosti se upisuju u UTC, uz spajanje sa postojećim danom.</span></div></div>
                        </div>
                    </EmptyState>
                </div>
            ) : !items.length ? (
                <div className="panel">
                    <EmptyState icon="pi pi-folder-open" title="Izaberite folder sa listićima"
                        text="Motor pronalazi listić na slici, poravnava skalu i čita traku aktivnosti: vožnja, srednja traka, odmor i početak aktivnosti. Vozač se uzima iz naziva fajla/foldera; AI čitanje datuma sa rukopisa se može isključiti.">
                        <div className="steps">
                            <div className="step"><span className="step-num">1</span><div><b>Kompanija</b><span>Izaberite kompaniju — vozač se prepoznaje po nazivu fajla/foldera među njenim vozačima.</span></div></div>
                            <div className="step"><span className="step-num">2</span><div><b>Datum aktivnosti</b><span>AI ga čita iz rukopisa i niza listića; početak aktivnosti motor sam pronalazi.</span></div></div>
                            <div className="step"><span className="step-num">3</span><div><b>Pregled</b><span>Strelicama ← → listajte; crveno je dan koji već ima aktivnosti.</span></div></div>
                        </div>
                    </EmptyState>
                </div>
            ) : (
                <div className={`bulk${jedan ? ' jedan' : ''}`}>
                    {!jedan && (
                    <nav className="panel bulk-list" aria-label="Spisak listića">
                        <ol>
                            {items.map((x, i) => {
                                const st = statusOf(x, planovi.get(x.key));
                                return (
                                    <li key={x.key}>
                                        <button type="button" className={`bulk-item${i === idx ? ' active' : ''}`} onClick={() => setIdx(i)} aria-current={i === idx ? 'true' : undefined}>
                                            {x.urls.thumb ? <img src={x.urls.thumb} alt="" /> : <span className="bulk-thumb" aria-hidden="true" />}
                                            <span className="bulk-item-text">
                                                <b title={x.name}>{x.name}</b>
                                                <span>{x.datumOd ? fmtNaive(naive(x.datumOd), false) : '—'}{x.driver ? ` · ${x.driver.lastName || driverLabel(x.driver)}` : ''}</span>
                                            </span>
                                            {aiUkljucen && x.status === 'spreman' && !STANJE[x.stanje] && (x.urls.aiD1 && !x.aiDat)
                                                && <i className="pi pi-sparkles bulk-ai-radi" title="AI čita rukopis…" aria-label="AI čita rukopis" />}
                                            <span className={`bulk-status ${st.cls}`} title={st.label}><i className={st.icon} aria-hidden="true" /><span className="sr-only">{st.label}</span></span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </nav>
                    )}

                    {cur && (
                        <ZastitaPregleda kljuc={cur.key} onVrati={() => update(cur.key, { aktivnosti: null, rucno: null, naciniDana: null })}>
                        <Pregled item={cur} par={parovi.get(cur.key)} aiUkljucen={aiUkljucen} izmena={izmenaForme} firma={!!firmaKljuc} idx={idx} active={active} aiDatumi={kandidatiDatuma(cur)}
                            predlog={predlogDatuma(items, idx, (x) => planovi.get(x.key))} count={items.length} plan={plan} srednja={srednja} busy={busy}
                            driverOptions={driverOptions} field={field} setIdx={setIdx}
                            onSave={sacuvajTekuci} onIzbaci={izbaciTekuci} onUkloni={ukloni}
                            onPolozaj={(polozaj) => { update(cur.key, { status: 'obrada', polozaj }); obrada.dodaj([{ key: cur.key, file: cur.file, polozaj }], { prioritet: true }); }}
                            onPreskoci={() => { update(cur.key, { stanje: 'preskocen' }); if (idx < items.length - 1) setIdx(idx + 1); }}
                            onVrati={() => update(cur.key, { stanje: 'novo', provera: null })} />
                        </ZastitaPregleda>
                    )}
                </div>
            )}
        </div>
    );
}

const fmtApi = (ms) => {
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:00`;
};

/** Greška u prikazu jednog listića ne sme da obori ceo ekran (ranije: prazna stranica) — poruka i povratak izmena listića */
class ZastitaPregleda extends React.Component {
    constructor(props) { super(props); this.state = { greska: null }; }

    static getDerivedStateFromError(greska) { return { greska }; }

    componentDidUpdate(prev) { if (prev.kljuc !== this.props.kljuc && this.state.greska) this.setState({ greska: null }); } // eslint-disable-line react/no-did-update-set-state

    componentDidCatch(greska, info) { console.error('Prikaz listića:', greska, info && info.componentStack); } // eslint-disable-line no-console

    render() {
        if (!this.state.greska) return this.props.children;
        return (
            <section className="panel bulk-view" role="alert">
                <div className="callout danger"><i className="pi pi-times-circle" /> Greška u prikazu listića: {String(this.state.greska.message || this.state.greska)}</div>
                <Button label="Vrati prepoznate aktivnosti i pokušaj ponovo" icon="pi pi-undo" outlined
                    onClick={() => { this.props.onVrati(); this.setState({ greska: null }); }} />
            </section>
        );
    }
}

/** dan već ima unete aktivnosti (osim odmora) van perioda listića — listić se DODAJE u taj dan (npr. 1. smena uneta, listić 3. smene) */
const dodajeSe = (d) => d.postoji && d.postojeciDetalji.some((x) => x.activityType !== 'break/rest' && (x.do <= d.od || x.od >= d.do));

/** natpis dugmeta za unos: broj dana i "dodaj" kad se listić dodaje u već unet dan */
function labelUnesi(plan) {
    if (!plan) return 'Unesi';
    const dani = plan.dani.filter((d) => !d.preskocen);
    const dodaj = dani.some(dodajeSe);
    if (dani.length > 1) return `Unesi ${dani.length} dana${dodaj ? ' – dodaj' : ''}`;
    return dodaj ? 'Unesi – dodaj' : 'Unesi';
}

const ALATI = [['driving', 'pi pi-car', 'Vožnja'], ['availability', 'pi pi-clock', 'Raspoloživost'], ['work', 'pi pi-briefcase', 'Ostali rad']];

/** jedan listić: slika sa aktivnostima (izmena kao u pojedinačnoj obradi), traka, podaci, tabela aktivnosti i dani za upis */
function Pregled({ item, par, aiUkljucen, izmena, firma, idx, predlog, aiDatumi, count, plan, srednja, busy, driverOptions, field, setIdx, onSave, onIzbaci, onUkloni, onPreskoci, onVrati, onPolozaj, active }) {
    const r = item.result;
    const zavrsen = !!STANJE[item.stanje];
    const proveren = item.provera && plan && item.provera.kljuc === kljucProvere(item, plan);

    const set = (patch) => izmena(item.key, patch);
    const uneto = ['sacuvan', 'zamenjen', 'izbaciti'].includes(item.stanje);
    const nedostaje = !item.datumOd ? 'Unesite datum aktivnosti.' : !item.driver ? 'Izaberite vozača.'
        : r && !plan ? 'Prazan listić — nema zapisa za unos. Izbacite ga, ili pomerite početak aktivnosti ako je listić bio uložen.'
        : !proveren ? 'Provera postojećih aktivnosti…'
            : plan && plan.neispravno ? 'Suma ručno izmenjenog dana nije 24h.'
                : plan && plan.prazno ? 'Nijedan dan nije izabran za unos.'
                    : plan && plan.kartica ? 'Listić se preklapa sa aktivnostima sa kartice — unos nije moguć (kartica se ne menja).'
                    : plan && plan.ponisti && plan.ponisti.length ? `Zamenjuje ceo prethodni listić: ${plan.ponisti.map((x) => x.listic || `#${x.id}`).join(', ')} (sve njegove aktivnosti postaju odmor).`
                    : plan && plan.zamena ? 'Postojeće aktivnosti u periodu listića biće zamenjene (izbor po danu je ispod).'
                        : plan && plan.konflikt ? 'Postojeće aktivnosti ostaju, listić popunjava samo odmor.' : 'Spremno za unos.';
    /* pomeraj Beograda za dan aktivnosti (zimi 1 h, leti 2 h). Sve na ovom listiću se PRIKAZUJE i UNOSI u UTC
       (slika okrenuta za pomak, traka, tabela, vreme ulaganja); interno ostaje vreme sa listića (plan.js). */
    const utcPomeraj = item.datumOd ? beogradOffset(naive(item.datumOd, 12 * 60)) / 3600000 : null;
    const off = utcPomeraj != null ? utcPomeraj * 60 : 0;
    const uUtc = (m) => (((m - off) % 1440) + 1440) % 1440;
    const izUtc = (m) => (m + off) % 1440;
    const ulaganjeUtc = uUtc(ulaganjeOf(item));
    /* aktivnosti koje su već u bazi u periodu listića — red "U bazi" ispod trake (Bulk i Taho listići) */
    const postojece = useMemo(() => (proveren ? postojeceNaTraci(item, plan) : []), [proveren, item.provera, plan]); // eslint-disable-line react-hooks/exhaustive-deps
    const ulaganjeDate = useMemo(() => { const d = new Date(2000, 0, 1); d.setHours(Math.floor(ulaganjeUtc / 60), ulaganjeUtc % 60, 0, 0); return d; }, [ulaganjeUtc]);

    /* ---------- aktivnosti listića (izmena na slici i u tabeli) ---------- */
    /* memo: nova lista pri svakom iscrtavanju je za DiscView "nove aktivnosti" i prekida započeto crtanje/vučenje (alat se gasi posle prvog klika) */
    const aktivnosti = useMemo(() => (r ? pomeri(aktivnostiOf(item), -off) : []), [r, item.aktivnosti, off]); // eslint-disable-line react-hooks/exhaustive-deps
    const segmenti = useMemo(() => (r ? pomeri(segmentiOf(item), -off) : []), [r, item.aktivnosti, item.ulaganje, item.datumOd, off]); // eslint-disable-line react-hooks/exhaustive-deps
    const [alat, setAlat] = useState(null);
    const [izabran, setIzabran] = useState(null);
    const [namesti, setNamesti] = useState(null); // ručno nameštanje listića (krug, rotacija, obrazac)
    useEffect(() => { setAlat(null); setIzabran(null); setNamesti(null); }, [item.key]);
    const promeniDatum = (d) => set({ datumOd: d, predlozen: false, datumIzvor: 'rucno', rucno: null, naciniDana: null });
    const postavi = (lista) => { set({ aktivnosti: pomeri(lista.filter((a) => a.end > a.start), off) }); setIzabran(null); };
    /* gornji alati samo crtaju; izabrana aktivnost se pretvara posebnim dugmićima ispod "Obriši" */
    const alatKlik = (tip) => { setIzabran(null); setAlat((t) => (t === tip ? null : tip)); };
    const pretvoriIzabranu = (tip) => { if (izabran != null && aktivnosti[izabran]) postavi(aktivnosti.map((a, i) => (i === izabran ? { ...a, type: tip } : a))); };
    const obrisiIzabranu = () => { if (izabran != null) postavi(aktivnosti.filter((_, i) => i !== izabran)); };
    /* Delete briše izabranu aktivnost, Esc prekida crtanje/izbor (kao u pojedinačnoj obradi) */
    useEffect(() => {
        const onKey = (e) => {
            if (!active || zavrsen) return;
            const tag = (e.target && e.target.tagName) || '';
            if (/INPUT|TEXTAREA|SELECT/.test(tag)) return;
            if (e.key === 'Delete' && izabran != null) { e.preventDefault(); obrisiIzabranu(); }
            if (e.key === 'Escape') { setAlat(null); setIzabran(null); }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    });
    const ukupno = aktivnosti.reduce((m, s) => ({ ...m, [s.type]: (m[s.type] || 0) + (s.end - s.start) }), {});

    return (
        <section className="panel bulk-view" aria-label={`Listić ${idx + 1} od ${count}`}>
            <div className="bulk-nav">
                <Button icon="pi pi-chevron-left" rounded text aria-label="Prethodni listić (strelica levo)" tooltip="Prethodni (←)" disabled={idx === 0} onClick={() => setIdx(idx - 1)} />
                <div className="bulk-nav-title">
                    <b>{idx + 1} / {count}</b>
                    <span title={item.name}>{item.name}</span>
                </div>
                <Button icon="pi pi-chevron-right" rounded text aria-label="Sledeći listić (strelica desno)" tooltip="Sledeći (→)" disabled={idx >= count - 1} onClick={() => setIdx(idx + 1)} />
                <span className="toolbar-sep" aria-hidden="true" />
                <Button icon="pi pi-times" rounded text severity="secondary" aria-label="Ukloni listić iz bulk-a" tooltip="Ukloni iz spiska" onClick={onUkloni} />
            </div>

            {item.status === 'obrada' && <EmptyState compact icon="pi pi-spin pi-spinner" title="Obrada slike…" />}
            {item.status === 'greska' && <div className="callout danger" role="alert"><i className="pi pi-times-circle" /> {item.error}</div>}

            {r && (
                <>
                    {r.upozorenja.map((u) => <div key={u} className="callout warn"><i className="pi pi-exclamation-triangle" /> {u}</div>)}
                    {item.provera && item.provera.duplikat && (
                        <div className="callout warn"><i className="pi pi-copy" /> Ista slika je već obrađena za ovog vozača ({item.provera.duplikat.fileName}, status {item.provera.duplikat.status}).</div>
                    )}
                    <div className="bulk-body">
                        <div className="bulk-disc">
                            <div className="bulk-disc-wrap">
                                {namesti ? (
                                    <NamestiListic src={item.urls.analiza} width={item.width} height={item.height} pocetni={namesti} pomak={off} utc={utcPomeraj != null} krugovi={r.krugovi || []}
                                        onOtkazi={() => setNamesti(null)} onPrimeni={(p) => { setNamesti(null); onPolozaj(p); }} />
                                ) : (
                                <DiscView src={item.urls.analiza} width={item.width} height={item.height} result={r} segments={segmenti} aktivnosti={aktivnosti}
                                    ulaganje={ulaganjeUtc} srednja={srednja} pomak={off} utc={utcPomeraj != null} postojece={postojece}
                                    onAktivnosti={zavrsen ? null : postavi} alat={alat} onAlatGotov={() => setAlat(null)} izabran={izabran} onIzabran={setIzabran}
                                    onUlaganje={zavrsen ? undefined : (m) => set({ ulaganje: izUtc(m), rucno: null, naciniDana: null })} />
                                )}
                                {!zavrsen && !namesti && (
                                    <div className="tool-rail right bulk-tools" role="toolbar" aria-label="Izmena aktivnosti">
                                        {ALATI.map(([tip, icon, naziv]) => (
                                            <Button key={tip} type="button" icon={icon} className={`tool-btn${alat === tip ? ' active' : ''}`} style={{ '--tool-color': `var(--ui-act-${tip})` }}
                                                tooltip={`Nacrtaj: ${naziv}`} tooltipOptions={{ position: 'left' }}
                                                aria-label={`Nacrtaj: ${naziv}`} onClick={() => alatKlik(tip)} />
                                        ))}
                                        <span className="rail-sep" aria-hidden="true" />
                                        <Button type="button" icon="pi pi-trash" className="rail-btn" disabled={izabran == null} tooltip="Obriši izabranu (Delete)" tooltipOptions={{ position: 'left' }}
                                            aria-label="Obriši izabranu aktivnost" onClick={obrisiIzabranu} />
                                        {/* pretvaranje izabrane aktivnosti: iste ikone kao alati, u obrisu; aktivni tek kad je aktivnost izabrana na listiću */}
                                        {ALATI.map(([tip, icon, naziv]) => (
                                            <Button key={`p-${tip}`} type="button" icon={icon} className="tool-btn pretvori" style={{ '--tool-color': `var(--ui-act-${tip})` }}
                                                disabled={izabran == null || !aktivnosti[izabran] || aktivnosti[izabran].type === tip}
                                                tooltip={`Pretvori izabranu u: ${naziv}`} tooltipOptions={{ position: 'left' }}
                                                aria-label={`Pretvori izabranu aktivnost u: ${naziv}`} onClick={() => pretvoriIzabranu(tip)} />
                                        ))}
                                        <span className="rail-sep" aria-hidden="true" />
                                        <Button type="button" icon="pi pi-arrows-alt" className="rail-btn" tooltip="Namesti listić ručno (loš snimak)" tooltipOptions={{ position: 'left' }}
                                            aria-label="Ručno namesti položaj listića" onClick={() => setNamesti({ cx: r.disc.cx, cy: r.disc.cy, r: r.disc.r, rotMin: r.rotMin, tip: r.tip || 'kienzle' })} />
                                        <Button type="button" icon="pi pi-undo" className="rail-btn" disabled={!item.aktivnosti && item.ulaganje === r.ulaganje} tooltip="Vrati prepoznato" tooltipOptions={{ position: 'left' }}
                                            aria-label="Vrati prepoznate aktivnosti" onClick={() => { set({ aktivnosti: null, rucno: null, ulaganje: r.ulaganje }); setIzabran(null); }} />
                                    </div>
                                )}
                            </div>
                            <p className="bulk-disc-help">
                                <b>Vožnja {fmtDuration((ukupno.driving || 0) * 60000)} · Raspoloživost {fmtDuration((ukupno.availability || 0) * 60000)}{ukupno.work ? ` · Ostali rad ${fmtDuration(ukupno.work * 60000)}` : ''}</b>
                                {item.aktivnosti ? ' (izmenjeno)' : ''}
                                <br />
                                <span className="muted">Desni klik na listić: lupa (uvećanje ispod miša) — desni klik ponovo, Esc ili klik van listića je gasi.</span>
                                <br />
                                {alat ? 'Kliknite na početak i vucite do kraja nove aktivnosti (Esc prekida).'
                                    : 'Vucite granicu aktivnosti mišem; klik na luk bira aktivnost, klik pored skida izbor (alat menja tip, Delete briše).'}
                            </p>
                            <div className="legend" aria-hidden="true">
                                <span><i style={{ '--legend-color': 'var(--ui-act-driving)' }} />Vožnja</span>
                                <span><i style={{ '--legend-color': 'var(--ui-act-availability)' }} />Raspoloživost</span>
                                <span><i style={{ '--legend-color': 'var(--ui-act-work)' }} />Ostali rad</span>
                                <span><i className="legend-gap" />Listić nije bio uložen</span>
                                {postojece.length > 0 && <span><i className="legend-baza" />Već u bazi — šrafirano, boja = aktivnost</span>}
                                <span><i style={{ '--legend-color': 'var(--ui-accent)' }} />Početak aktivnosti {fmtMin(ulaganjeUtc)}{utcPomeraj != null ? ' UTC' : ''}</span>
                                <span>{utcPomeraj != null ? `Prikaz u UTC: listić je okrenut za ${utcPomeraj} h (${utcPomeraj === 2 ? 'letnje' : 'zimsko'} vreme)` : 'Vreme sa listića — UTC prikaz posle unosa datuma'}</span>
                            </div>
                        </div>
                        <div className="bulk-form">
                            <div className="form-grid cols-2 datum-pocetak">
                                {field(<>Datum aktivnosti<span className="req" aria-hidden="true">*</span></>, `bv-od-${item.key}`, (
                                    <div className="datum-korak">
                                        {/* dan ranije / kasnije: brza ispravka kad AI ili niz promaši datum za dan */}
                                        <Button type="button" icon="pi pi-chevron-left" className="p-button-outlined p-button-secondary" disabled={zavrsen || !item.datumOd}
                                            aria-label="Dan ranije" tooltip="Dan ranije" tooltipOptions={{ position: 'top' }} onClick={() => promeniDatum(addDays(item.datumOd, -1))} />
                                        <DateInput inputId={`bv-od-${item.key}`} value={item.datumOd} disabled={zavrsen} showIcon
                                            onChange={(e) => promeniDatum(e.value)} className={!item.datumOd ? 'p-invalid' : ''} />
                                        <Button type="button" icon="pi pi-chevron-right" className="p-button-outlined p-button-secondary" disabled={zavrsen || !item.datumOd}
                                            aria-label="Dan kasnije" tooltip="Dan kasnije" tooltipOptions={{ position: 'top' }} onClick={() => promeniDatum(addDays(item.datumOd, 1))} />
                                    </div>
                                ))}
                                {/* početak aktivnosti (kada je listić uložen) prepoznaje motor; operater ga može pomeriti (ovde ili prevlačenjem zelene linije) */}
                                <div className="field" title={`Početak aktivnosti${utcPomeraj != null ? ' (UTC)' : ''} prepoznaje motor; ako je pogrešan, upišite vreme ili prevucite zelenu liniju na listiću.`}>
                                    {/* kratka labela — uska kolona, mora stati u jedan red (iste visine kao Datum aktivnosti) */}
                                    <label htmlFor={`bv-ul-${item.key}`}>Početak <i className="pi pi-info-circle muted" aria-hidden="true" /></label>
                                    <DateInput inputId={`bv-ul-${item.key}`} value={ulaganjeDate} timeOnly disabled={zavrsen}
                                        onChange={(e) => { if (e.value instanceof Date) set({ ulaganje: izUtc(e.value.getHours() * 60 + e.value.getMinutes()), rucno: null, naciniDana: null }); }} />
                                    {item.ulaganje !== r.ulaganje && <small className="muted">Pomereno ručno (motor: {fmtMin(uUtc(r.ulaganje))})</small>}
                                </div>
                            </div>
                            <AiDatum item={item} zavrsen={zavrsen} aiUkljucen={aiUkljucen} predlog={predlog} aiDatumi={aiDatumi}
                                onIzbor={(d) => set({ datumOd: d, predlozen: false, datumIzvor: 'rucno', rucno: null, naciniDana: null })} />
                            {par && !zavrsen && (
                                <div className="callout warn"><i className="pi pi-users" />
                                    <span>Listić {par.name} ima isti početak i kraj aktivnosti (udvojena posada?), ali datum {fmtNaive(naive(par.datumOd), false)} — proverite datum.</span>
                                </div>
                            )}
                            {item.urls.datumi && <img className="hand-crop" src={item.urls.datumi} alt="Datumi napisani na listiću" />}
                            {plan && (
                                <p className="bulk-period">
                                    Listić: <b>{fmtNaive(plan.lokalno.od)}</b> – <b>{fmtNaive(plan.lokalno.do)}</b> (Beograd)
                                    <br />Upisuje se: {fmtNaive(plan.utc.od)} – {fmtNaive(plan.utc.do)} (UTC)
                                </p>
                            )}
                            {field(<>Vozač<span className="req" aria-hidden="true">*</span></>, `bv-d-${item.key}`, (
                                <Dropdown inputId={`bv-d-${item.key}`} value={item.driver} options={driverOptions} dataKey="id" filter disabled={zavrsen}
                                    onChange={(e) => set({ driver: e.value, provera: null })} placeholder="Izaberite vozača" emptyMessage="Nema pronađenih vozača"
                                    className={!item.driver ? 'p-invalid' : ''}
                                    virtualScrollerOptions={driverOptions.length > 10 ? { itemSize: 40 } : undefined} />
                            ), (
                                <>
                                    {item.urls.ime && <img className="hand-crop" src={item.urls.ime} alt="Ime vozača napisano na listiću" />}
                                    <AiVozac item={item} driverOptions={driverOptions} onIzbor={(d) => set({ driver: d, provera: null })} />
                                </>
                            ))}
                            <div className={`bulk-unos${uneto ? ' done' : ''}`}>
                                <div className="bulk-unos-status">
                                    {uneto
                                        ? <span className={`pill ${STANJE[item.stanje].cls}`}><i className={STANJE[item.stanje].icon} /> {STANJE[item.stanje].label}</span>
                                        : <span className="pill neutral"><i className="pi pi-circle" /> {item.stanje === 'preskocen' ? 'Preskočen — nije uneto' : 'Nije uneto'}</span>}
                                    {!zavrsen && <span className="bulk-unos-hint">{nedostaje}</span>}
                                </div>
                                {zavrsen ? (
                                    item.stanje === 'preskocen' && <Button label="Vrati u obradu" icon="pi pi-undo" text onClick={onVrati} />
                                ) : (
                                    <div className="bulk-unos-actions">
                                        {plan && plan.zamena && !plan.kartica ? (
                                            <Button label={plan.dani.filter((d) => !d.preskocen).length > 1 ? 'Unesi dane i zameni' : 'Unesi i zameni'} icon="pi pi-sync" severity="danger"
                                                disabled={!proveren || plan.neispravno || busy} onClick={() => onSave(true)} />
                                        ) : (
                                            <Button label={labelUnesi(plan)} icon={plan && plan.dani.some((d) => !d.preskocen && dodajeSe(d)) ? 'pi pi-plus' : 'pi pi-check'}
                                                disabled={!plan || plan.prazno || plan.neispravno || !item.driver || !proveren || busy} onClick={() => onSave(false)} />
                                        )}
                                        <Button label="Izbaci listić" icon="pi pi-trash" outlined severity="warning" disabled={!plan || !item.driver || busy} onClick={onIzbaci} />
                                        <Button label="Preskoči" icon="pi pi-forward" text severity="secondary" onClick={onPreskoci} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <TraceStrip src={item.urls.strip} segments={segmenti} ulaganje={ulaganjeUtc} srednja={srednja} pomak={off} postojece={postojece}
                        onUlaganje={null} />

                    {plan ? (
                        <div className="dani-listica">
                            {plan.dani.map((d) => (
                                <DanListica key={d.dan} d={d} proveren={proveren} zavrsen={zavrsen}
                                    onNacin={(n) => set({ naciniDana: { ...(item.naciniDana || {}), [d.dan]: n } })}
                                    onRucno={(l) => {
                                        /* obrisane sve aktivnosti dana (ostao samo odmor) -> dan se ne unosi (vlasnik: brisanje aktivnosti drugog dana = zanemari taj dan) */
                                        const patch = izmenaDana(item, d, l, srednja);
                                        if (l.every((x) => x.activityType === 'break/rest')) patch.naciniDana = { ...(item.naciniDana || {}), [d.dan]: 'preskoci' };
                                        set(patch); setIzabran(null);
                                    }}
                                    onVrati={() => { const rr = { ...(item.rucno || {}) }; delete rr[d.dan]; set({ rucno: rr }); }} />
                            ))}
                        </div>
                    ) : (
                        <div className="callout info"><i className="pi pi-calendar" /> Dani listića (šta postoji, šta donosi listić i dan posle unosa) prikazuju se posle unosa datuma aktivnosti.</div>
                    )}
                </>
            )}
        </section>
    );
}

/** datum aktivnosti: odakle je (AI / niz) i najverovatniji datumi iz rukopisa (klik bira) */
function AiDatum({ item, zavrsen, aiUkljucen, predlog, aiDatumi, onIzbor }) {
    if (zavrsen) return null;
    if (!aiUkljucen) return <p className="ai-hint"><i className="pi pi-power-off" aria-hidden="true" /> AI čitanje je isključeno — datum unesite ručno.</p>;
    const isti = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    return (
        <>
            {item.predlozen && item.datumOd && (
                <div className="callout warn"><i className="pi pi-eye" />
                    <span>{item.datumNacin === 'niz' ? 'Predložen datum: nastavak prethodnog listića istog vozača' : 'Datum je predložio AI iz rukopisa'} — uporedite sa rukopisom ispod.</span>
                </div>
            )}
            {!item.urls.aiD1 ? null : !aiDatumi ? (
                <p className="ai-hint"><i className="pi pi-spin pi-spinner" /> AI čita datum…</p>
            ) : (
                <div className="ai-predlozi">
                    <span className="ai-hint"><i className="pi pi-sparkles" /> AI čita:</span>
                    {aiDatumi.map((d) => (
                        <button key={d.getTime()} type="button" className={`ai-chip${isti(d, item.datumOd) ? ' active' : ''}`} onClick={() => onIzbor(d)}>
                            {fmtNaive(naive(d), false)}
                        </button>
                    ))}
                    {predlog && !aiDatumi.some((d) => isti(d, predlog.datum)) && (
                        <button type="button" className={`ai-chip${isti(predlog.datum, item.datumOd) ? ' active' : ''}`} onClick={() => onIzbor(predlog.datum)}
                            title={`Nastavak listića ${predlog.od}`}>{fmtNaive(naive(predlog.datum), false)} (niz)</button>
                    )}
                </div>
            )}
        </>
    );
}

/** vozač iz naziva fajla/foldera; kad naziv nije tačno ime vozača — do tri najsličnija vozača (klik bira) */
function AiVozac({ item, driverOptions, onIzbor }) {
    if (item.driverIzvor === 'naziv' && item.driver) {
        return <p className="ai-hint ok" title={item.putanja || item.name}><i className="pi pi-file" /> Vozač prema nazivu fajla / foldera.</p>;
    }
    const predlozi = predloziIzNaziva(item.putanja || item.name, driverOptions.map((o) => o.value));
    if (!predlozi.length) return <p className="ai-hint"><i className="pi pi-info-circle" aria-hidden="true" /> Naziv fajla nije ime vozača izabranih kompanija — izaberite vozača.</p>;
    const label = (v) => (driverOptions.find((o) => o.value.id === v.id) || {}).label;
    return (
        <div className="ai-predlozi">
            <span className="ai-hint"><i className="pi pi-file" /> Slično nazivu fajla:</span>
            {predlozi.map(({ vozac }) => (
                <button key={vozac.id} type="button" className={`ai-chip${item.driver && item.driver.id === vozac.id ? ' active' : ''}`}
                    onClick={() => onIzbor(vozac)}>{label(vozac)}</button>
            ))}
        </div>
    );
}
